// Pixar Talking Ad's Cast & set stage (live's `cast-stage` and `pixar-still-card`; not captured,
// since live's stage needs a paid Approve script, so built from live's code): "Cast & set", then
// the draft's stills, the character sheet wide and the frames and the end card 9:16, each with
// its ↻; a frame whose clip the last run's video model refused carries live's notice, the end
// card's with "Build without end card" (Pixar Talking Ad-2).
import { Button, Group, Stack, Text } from "@mantine/core";
import { useState } from "react";

import type { PixarDraftStill, PixarDraftStillName } from "../../../api/generated/model";
import { MediaPreview, type PreviewItem } from "../../../platform/media/MediaPreview";
import { ConfirmModal } from "../../../ui/ConfirmModal";
import { StillCard } from "./StillCard";
import { BUILD_WITHOUT_END_CARD, stillWaiting } from "./pixar-draft";
import classes from "./podcast.module.css";

/** Live's labels (`core/constants.ts`, `STILL_LABELS`). */
export const STILL_LABELS: Readonly<Record<PixarDraftStillName, string>> = {
  char_sheet: "Character",
  opening: "Opening",
  hold: "Hold",
  closing: "Closing",
  end_card: "End card",
};

/** The character chain in display order, then the end card (live's `STILL_GRAPH.order`). */
const CHAIN: readonly PixarDraftStillName[] = ["char_sheet", "opening", "hold", "closing"];
const PENDING = { status: "pending", url: null, problem: null } as const;

/** A made still in the shared lightbox (issue 828, bug 25), named by live's label. */
function stillItem(still: PixarDraftStill & { url: string }): PreviewItem {
  return {
    id: still.name,
    name: STILL_LABELS[still.name],
    kind: "image",
    url: still.url,
    thumb: still.url,
    poster: null,
    durationMs: null,
    width: null,
    height: null,
  };
}

export interface PixarCastStageProps {
  /** The draft's stills: the chain, and the end card with the end card on. */
  stills: readonly PixarDraftStill[];
  /** The end card is on: its still is drawn too (shown waiting while the shots are planned). */
  endCard: boolean;
  /** The shot director plans the shots: every still waits on it. */
  planning: boolean;
  busy: boolean;
  resetting: boolean;
  /** Live's refused-take notice on each frame of a clip the last run's video model refused. */
  flags: Readonly<Partial<Record<PixarDraftStillName, string>>>;
  /** "Build without end card", offered on a refused end card; null where it cannot start. */
  onBuildWithoutEndCard: (() => void) | null;
  onRegenerate: (name: PixarDraftStillName) => void;
  onStartOver: () => void;
}

/**
 * Live's cast stage: "Cast & set" and a row of the draft's stills, each with its label under it.
 * A done still opens in the shared lightbox, stepping through the made ones. Ours also offers
 * "Start over" (the draft outlives the panel, where live's closing the panel was its only way
 * back), as Street Interview Ad's.
 */
export function PixarCastStage(props: PixarCastStageProps) {
  const { stills, endCard, planning, busy, resetting, flags, onBuildWithoutEndCard, onRegenerate, onStartOver } = props;
  const [preview, setPreview] = useState<PixarDraftStillName | null>(null);
  // Before the shot director's spec, the stills it will start (live's `stillNames`).
  const shown =
    stills.length > 0 ? stills.map((still) => still.name) : [...CHAIN, ...(endCard ? ["end_card" as const] : [])];
  const [confirming, setConfirming] = useState(false);
  const made = stills.flatMap((still) => (still.url ? [{ ...still, url: still.url }] : []));
  return (
    <Stack gap={12} aria-label="Cast & set" component="section">
      <Group justify="space-between" wrap="nowrap">
        <Text className={classes.castTitle}>Cast &amp; set</Text>
        <Button
          variant="subtle"
          size="compact-sm"
          disabled={resetting || busy || planning}
          onClick={() => setConfirming(true)}
        >
          Start over
        </Button>
      </Group>
      <div className={classes.stills}>
        {shown.map((name) => (
          <StillCard
            key={name}
            label={STILL_LABELS[name]}
            still={stills.find((each) => each.name === name) ?? PENDING}
            waiting={planning || stillWaiting(name, stills)}
            busy={busy}
            wide={name === "char_sheet"}
            flag={flags[name] ?? null}
            action={
              name === "end_card" && flags[name] !== undefined
                ? { label: BUILD_WITHOUT_END_CARD, disabled: busy || onBuildWithoutEndCard === null, onClick: () => onBuildWithoutEndCard?.() }
                : null
            }
            onRegenerate={() => onRegenerate(name)}
            onFixLabel={null}
            onPreview={() => setPreview(name)}
          />
        ))}
      </div>
      <MediaPreview
        items={made.map(stillItem)}
        current={preview}
        onShow={(name) => setPreview(name as PixarDraftStillName | null)}
      />
      <ConfirmModal
        opened={confirming}
        title="Start over"
        confirmLabel="Start over"
        pending={resetting}
        onCancel={() => setConfirming(false)}
        onConfirm={() => {
          onStartOver();
          setConfirming(false);
        }}
      >
        <Text size="sm">The script, the shot spec and the five images are cleared, and the form starts empty.</Text>
      </ConfirmModal>
    </Stack>
  );
}
